import React, { useState, useEffect } from 'react'
import {
	Card,
	CardContent,
	CardHeader,
	Checkbox,
	Collapse,
	IconButton,
	ListItemText,
	makeStyles,
	MenuItem,
	Select,
	TextField
} from '@material-ui/core'
import { ExpandMoreOutlined } from '@material-ui/icons'
import { DeletableChip } from '../../Misc/DeletableChip'
import Autocomplete from '@material-ui/lab/Autocomplete'
import clsx from 'clsx'
import { cloneDeep } from 'lodash'
import useConstants from '../../../hooks/useConstants'

const useStyles = makeStyles((theme) => ({
	header: {
		padding: '13px 24px',
		userSelect: 'none',
		cursor: 'pointer',
		'& .MuiCardHeader-action': {
			margin: 0
		}
	},
	grid: {
		display: 'grid',
		gridGap: '23px',
		marginBottom: '23px',
		'&:last-child': {
			marginBottom: 0
		}
	},
	gridDouble: {
		gridTemplateColumns: 'repeat(auto-fill, minmax(424px, 1fr))',
		'& .field-wrapper': {
			'& .MuiTextField-root': {
				minWidth: '424px !important'
			}
		}
	},
	gridTriple: {
		gridTemplateColumns: 'repeat(auto-fill, minmax(274px, 1fr))',
		'& .field-wrapper': {
			'& .MuiTextField-root': {
				minWidth: '274px !important'
			},
			'& .MuiFormControl-root': {
				minWidth: '272px !important'
			}
		}
	},
	expand: {
		transform: 'rotate(0deg)',
		marginLeft: 'auto',
		transition: theme.transitions.create('transform', {
			duration: theme.transitions.duration.shortest
		}),
		padding: 0
	},
	expandOpen: {
		transform: 'rotate(180deg)'
	},
	chips: {
		display: 'flex',
		flexWrap: 'wrap',
		marginTop: '6px',
		backgroundColor: 'transparent !important'
	},
	footerButton: {
		fontFamily: 'Nunito',
		fontWeight: '800',
		'& .MuiButton-label': {
			lineHeight: '19px',
			fontSize: '14px'
		}
	},
	autoComplete: {
		'& .MuiFilledInput-root': {
			paddingTop: '17px'
		},
		'& .Mui-focused': {
			'& .MuiAutocomplete-endAdornment svg': {
				fill: theme.palette.secondary.main
			}
		},
		'& .MuiAutocomplete-endAdornment svg': {
			fill: '#777679'
		}
	}
}))

const PetHealthCard = (props) => {
	const classes = useStyles()
	const { tags } = useConstants()

	let { pet, setPet } = props

	const [isExpanded, setIsExpanded] = useState(false)
	const [petTags, setPetTags] = useState([])

	useEffect(() => {
		if (tags && pet.tags && pet.tags.health && pet.tags.health.length) {
			let h = tags.health.filter((tag) => {
				return pet.tags.health.findIndex((pt) => pt._id === tag._id) !== -1
			})

			setPetTags(h)
		} else {
			setPetTags([])
		}
	}, [tags, pet])

	const updatePet = (field, value) => {
		setPet({ ...pet, [field]: value })
	}

	const updateHealthTags = (value) => {
		let updatedTags = cloneDeep(pet.tags)
		updatedTags['health'] = value

		updatePet('tags', updatedTags)
	}

	const removeTag = (value) => {
		let updatedTags = cloneDeep(pet.tags)
		let tagIndex = updatedTags['health'].findIndex((t) => t._id === value._id)

		if (tagIndex !== -1) {
			updatedTags['health'].splice(tagIndex, 1)
		}

		updatePet('tags', updatedTags)
	}

	return (
		<Card>
			<CardHeader
				title="Health"
				className={classes.header}
				onClick={() => setIsExpanded(!isExpanded)}
				action={
					<IconButton
						className={clsx(classes.expand, {
							[classes.expandOpen]: isExpanded
						})}
						onClick={() => setIsExpanded(!isExpanded)}
						aria-expanded={isExpanded}
						aria-label="show more">
						<ExpandMoreOutlined style={{ fill: '#777679' }} />
					</IconButton>
				}
			/>
			<Collapse in={isExpanded} timeout="auto" unmountOnExit>
				<CardContent>
					<div className={classes.grid}>
						<div className={clsx([classes.fieldWrapper, 'field-wrapper'])}>
							<Autocomplete
								multiple
								getOptionLabel={(option) => option.name || ''}
								disableCloseOnSelect
								size="small"
								id="health-tags"
								className={classes.autoComplete}
								options={tags ? tags.health : []}
								value={petTags && petTags.length ? petTags : []}
								onChange={(e, v) => updateHealthTags(v)}
								onClose={(e) => {
									e.target.value = ''
								}}
								disabled={!pet.active}
								renderTags={(selected) =>
									selected.map((value) => (
										<DeletableChip
											key={value._id}
											label={value.name}
											{...{
												onDelete: pet.active ? () => removeTag(value) : null
											}}
										/>
									))
								}
								renderOption={(option, { selected }) => (
									<React.Fragment>
										<Checkbox checked={selected} />
										<ListItemText
											style={{ textTransform: 'capitalize' }}
											primary={option.name}
										/>
									</React.Fragment>
								)}
								renderInput={(params) => (
									<TextField
										{...params}
										variant="filled"
										fullWidth
										label="Select All Health Tags"
									/>
								)}
							/>
						</div>
					</div>
					<div className={classes.grid}>
						<div className={clsx([classes.fieldWrapper, 'field-wrapper'])}>
							<TextField
								label="Allergies"
								variant="filled"
								value={pet.allergies || ''}
								disabled={!pet.active}
								onChange={(e) => updatePet('allergies', e.target.value)}
								fullWidth
							/>
						</div>
					</div>
					<div className={classes.grid}>
						<div className={clsx([classes.fieldWrapper, 'field-wrapper'])}>
							<TextField
								label="Health Notes"
								variant="filled"
								value={pet.health_notes || ''}
								disabled={!pet.active}
								onChange={(e) => updatePet('health_notes', e.target.value)}
								fullWidth
							/>
						</div>
					</div>
				</CardContent>
			</Collapse>
		</Card>
	)
}

export default PetHealthCard
